// AxisChat.js — the docked chat pane for the Axis Inspector. PER-SUBJECT threads: each pattern
// (candidate_id) gets its own conversation, created lazily on first ask and kept for the session, so a
// thread stays small and on-topic and context is seeded server-side just once (see
// AssistantController::axisInspectorChat). The "ask →" chips seed turn one; follow-ups continue that thread.

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { tip } from './Tip';

const NAVY = '#14536e', INK = '#0f172a', MUTE = '#64748b', LINE = '#e7edf1';
const RESPONSE_MODES = ['concise', 'detailed', 'deep'];

function savedResponseMode() {
  try {
    const saved = window.localStorage.getItem('sr-assistant-response-mode');
    return RESPONSE_MODES.includes(saved) ? saved : 'concise';
  } catch (e) {
    return 'concise';
  }
}

// The controller emits ordinary SSE over the request that created the chat.
// Fetch is used instead of EventSource because the question and private subject
// identifiers belong in a CSRF-protected POST body.
async function readEventStream(response, onEvent) {
  if (!response.body?.getReader) throw new Error('Streaming is unavailable in this browser.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const deliver = (block) => {
    const data = block.split(/\r?\n/)
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trimStart())
      .join('\n');
    if (!data) return;
    try { onEvent(JSON.parse(data)); } catch (e) { /* ignore malformed/incomplete metadata events */ }
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || '';
    blocks.forEach(deliver);
    if (done) break;
  }
  if (buffer.trim()) deliver(buffer);
}

// active/onInteract: the host page's "active panel" affordance — a navy top accent when the chat
// is where the answer just landed; any interaction inside claims it.
// full: phone mode — the chat fills its container (a full-screen sheet) instead of a 380px dock.
// store: OPTIONAL session store owned by the host ({ convos, setConvos, drafts, setDrafts }). With it,
// transcripts, chat ids and unsent drafts survive this component unmounting — collapsing the unit dock,
// stepping to the next scene, or closing the reading (owner, 2026-10-14). Without it, state is local.
// onSeedConsumed(nonce): the host clears its seed once THIS component has actually sent it, so a seed
// can never replay on remount; a seed that arrives mid-reply waits until the reply finishes.
// heading / closeGlyph / closeLabel: the unit dock collapses rather than closes.
// queue / onClaim: BOUND questions (owner, 2026-10-14 review I1). Each record is { id, question, subject }
// and is sent against ITS OWN subject (unit + axis), never whatever the panel shows at dispatch time —
// so a question asked about Scene 6 stays a Scene 6 question even if the writer has moved to Scene 7.
// The host removes a record when onClaim(id) is called, which happens exactly once, just before sending.
// The thread key for a subject — ONE definition, exported so the host can rebuild saved chats into the
// same keys when they reload (Icicle hydrates the session store from /assistant/axis-inspector-chats).
export const chatKeyOf = (subj) => (subj?.candidateId
  ? `pattern:${subj.axis || ''}:${subj.candidateId}`
  : subj?.unitId
    ? `unit:${subj.unitId}`
    : `axis:${subj?.axis || ''}`);

// "Conversations" — every thread you have on this script, newest first, one line each; clicking one
// takes you to that unit / axis / pattern with its chat open (owner, 2026-10-14: "there should also be
// a way to see units with chats"). The host builds the list and does the navigating.
function ConversationList({ items, currentKey, onOpen, onBack }) {
  const when = (t) => {
    if (!t) return '';
    const d = (Date.now() - t) / 1000;
    if (d < 90) return 'just now';
    if (d < 3600) return `${Math.round(d / 60)} min ago`;
    if (d < 86400) return `${Math.round(d / 3600)} h ago`;
    if (d < 172800) return 'yesterday';
    return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 10, minHeight: 0, background: '#f5f7f9' }}>
      <button type="button" onClick={onBack}
        style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 700, fontSize: 12.5, marginBottom: 8, display: 'inline-block' }}>‹ Back to this chat</button>
      {items.length === 0 && <div style={{ color: MUTE, fontSize: 13 }}>No conversations on this script yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map(it => {
          const on = it.key === currentKey;
          return (
            <button key={it.key} type="button" onClick={() => onOpen(it.key)} aria-current={on ? 'true' : undefined}
              style={{ textAlign: 'left', cursor: 'pointer', background: on ? '#eef4f8' : '#fff', border: `1px solid ${on ? NAVY : LINE}`,
                borderRadius: 10, padding: '8px 10px', color: INK }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                <span style={{ color: MUTE, fontSize: 11, whiteSpace: 'nowrap' }}>{when(it.updatedAt)}</span>
              </div>
              <div style={{ color: MUTE, fontSize: 11.5, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.detail ? `${it.detail} · ` : ''}{it.count} question{it.count === 1 ? '' : 's'}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
ConversationList.propTypes = { items: PropTypes.array, currentKey: PropTypes.string, onOpen: PropTypes.func, onBack: PropTypes.func };

export default function AxisChat({ movieHash, subject, seed, onClose, starters, active = false, onInteract, full = false, dockWidth = 460, overlay = false,
  store = null, onSeedConsumed = null, heading = 'Ask about this', closeGlyph = '✕', closeLabel = 'Close chat', autoFocus = false,
  queue = null, onClaim = null, onSend = null, conversations = null, onOpenConversation = null }) {
  const [showList, setShowList] = useState(false);
  const [localConvos, setLocalConvos] = useState({});   // { [subjectKey]: { chatId, msgs, followUps } }
  const convos = store ? store.convos : localConvos;
  const setConvos = store ? store.setConvos : setLocalConvos;
  const [localDraft, setLocalDraft] = useState('');
  const [sendingKeys, setSendingKeys] = useState({});   // { [subjectKey]: true } while a request is being opened
  const [responseMode, setResponseMode] = useState(savedResponseMode);
  // undefined = not received yet; null = unlimited plan; object = capped.
  const [usage, setUsage] = useState(undefined);
  const atLimit = usage && usage.remaining <= 0;
  const transcriptRef = useRef(null);
  const followStreamRef = useRef(true);
  const inputRef = useRef(null);
  // A pattern, a selected unit, and an axis-overview are different subjects.
  // A UNIT is ONE conversation (owner, 2026-10-14): the axis is per-turn context, sent with every
  // question, and the server attaches that axis's reading when the topic changes. (It used to be one
  // thread per unit+axis, which split a writer's questions about one scene across several threads.)
  const keyOf = chatKeyOf;
  const key = keyOf(subject);
  const convo = convos[key] || { chatId: null, msgs: [], followUps: [] };
  const input = store ? (store.drafts[key] || '') : localDraft;
  const setInput = (v) => { if (store) store.setDrafts(d => ({ ...d, [key]: v })); else setLocalDraft(v); };
  const lastMsg = convo.msgs[convo.msgs.length - 1];
  // A reply can still be streaming into the store after this component remounted, so "sending" is
  // also read off the conversation itself, per subject — a reply in Scene 6 does not lock Scene 7.
  const busyFor = (k) => {
    const last = (convos[k]?.msgs || [])[(convos[k]?.msgs || []).length - 1];
    return !!sendingKeys[k] || !!(last && !last.me && last.streaming);
  };
  const sending = busyFor(key);

  // subj defaults to the panel's subject; a bound queue record passes its own.
  const send = async (raw, seeded = false, subj = subject) => {
    const text = (raw || '').trim();
    const key = keyOf(subj);   // eslint-disable-line no-shadow -- the request's own conversation
    if (!text || !subj || busyFor(key)) return false;
    const subject = subj;      // eslint-disable-line no-shadow -- everything below builds from the bound subject
    const existingChatId = convos[key]?.chatId || null;
    const userMessageId = `user-${Date.now()}-${Math.random()}`;
    const answerMessageId = `answer-${Date.now()}-${Math.random()}`;
    // interaction tracking (no question text — just that a question was asked, on what)
    try { window.srTrack?.('chat_send', 'axis_chat', { axis: subject.axis, candidate_id: subject.candidateId || null, seeded, follow_up: (convos[key]?.msgs || []).length > 0 }); } catch (e) {}
    setSendingKeys(m => ({ ...m, [key]: true }));
    if (onSend) { try { onSend(subject); } catch (e) { /* host bookkeeping must never block a send */ } }
    followStreamRef.current = true;
    if (store) store.setDrafts(d => ({ ...d, [key]: (seeded ? (d[key] || '') : '') })); else if (!seeded) setLocalDraft('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setConvos(c => ({ ...c, [key]: {
      chatId: c[key]?.chatId || null,
      followUps: [],
      msgs: [
        ...(c[key]?.msgs || []),
        { id: userMessageId, me: true, text },
        { id: answerMessageId, me: false, markdown: '', streaming: true },
      ],
    } }));

    const updateAnswer = (updater) => {
      setConvos(c => {
        const current = c[key] || { chatId: null, msgs: [], followUps: [] };
        return { ...c, [key]: {
          ...current,
          msgs: current.msgs.map(message => message.id === answerMessageId ? updater(message) : message),
        } };
      });
    };

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const r = await fetch('/assistant/axis-inspector-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream, application/json', ...(token ? { 'X-CSRF-TOKEN': token } : {}) },
        body: JSON.stringify({
          movie_hash: movieHash,
          axis: subject.axis,
          candidate_id: subject.candidateId || '',
          unit_id: subject.unitId || '',
          question: text,
          chat_id: existingChatId,
          response_mode: responseMode,
          stream: true,
        }),
      });
      const contentType = r.headers.get('content-type') || '';

      // Validation, authorization, and interaction-limit errors remain JSON so
      // they can fail before a provider stream is opened.
      if (!r.ok || !contentType.includes('text/event-stream')) {
        const j = await r.json().catch(() => ({}));
        if (Object.prototype.hasOwnProperty.call(j, 'chat_usage')) setUsage(j.chat_usage);
        setConvos(c => {
          const current = c[key] || { chatId: existingChatId, msgs: [], followUps: [] };
          return { ...c, [key]: {
            ...current,
            chatId: (r.ok && j.chat_id) ? j.chat_id : current.chatId,
            followUps: r.ok && Array.isArray(j.follow_ups) ? j.follow_ups.slice(0, 3) : [],
            msgs: current.msgs.map(message => message.id === answerMessageId
              ? ((r.ok && (j.markdown || j.answer))
                ? (j.markdown
                  ? { ...message, markdown: j.markdown, html: undefined, streaming: false }
                  : { ...message, html: j.answer, markdown: undefined, streaming: false })
                : { ...message, text: j.error || 'Something went wrong — try again.', markdown: undefined, streaming: false })
              : message),
          } };
        });
        return;
      }

      let streamFailed = false;
      await readEventStream(r, (event) => {
        if (event.type === 'chat_started') {
          setConvos(c => ({ ...c, [key]: {
            ...(c[key] || { msgs: [], followUps: [] }),
            chatId: event.chat_id || c[key]?.chatId || null,
          } }));
        } else if (event.type === 'text_delta' && typeof event.delta === 'string') {
          updateAnswer(message => ({
            ...message,
            markdown: (message.markdown || '') + event.delta,
            streaming: true,
          }));
        } else if (event.type === 'follow_ups') {
          const questions = Array.isArray(event.questions)
            ? event.questions.filter(question => typeof question === 'string' && question.trim()).slice(0, 3)
            : [];
          setConvos(c => ({ ...c, [key]: {
            ...(c[key] || { chatId: existingChatId, msgs: [] }),
            followUps: questions,
          } }));
        } else if (event.type === 'chat_complete') {
          if (Object.prototype.hasOwnProperty.call(event, 'chat_usage')) setUsage(event.chat_usage);
          setConvos(c => ({ ...c, [key]: {
            ...(c[key] || { msgs: [], followUps: [] }),
            chatId: event.chat_id || c[key]?.chatId || null,
          } }));
          updateAnswer(message => ({ ...message, streaming: false }));
        } else if (event.type === 'error') {
          streamFailed = true;
          updateAnswer(message => message.markdown
            ? { ...message, markdown: `${message.markdown}\n\n_${event.message || 'The response was interrupted. Please try again.'}_`, streaming: false }
            : { ...message, text: event.message || 'The response was interrupted. Please try again.', markdown: undefined, streaming: false });
        }
      });

      if (!streamFailed) updateAnswer(message => ({ ...message, streaming: false }));
    } catch (e) {
      updateAnswer(message => message.markdown
        ? { ...message, markdown: `${message.markdown}\n\n_Connection interrupted — try again._`, streaming: false }
        : { ...message, text: 'Network error — try again.', markdown: undefined, streaming: false });
    } finally {
      setSendingKeys(m => { const n = { ...m }; delete n[key]; return n; });
    }
    return true;
  };

  // auto-send a seeded question (fired by an "ask →" chip). nonce guards against re-firing on re-render.
  // A seed that arrives while a reply is streaming WAITS (re-checked when `sending` clears) and is only
  // marked consumed once it has actually been sent — so it is neither dropped nor sent twice.
  const lastSeed = useRef(null);
  useEffect(() => {
    if (!seed || !seed.nonce || seed.nonce === lastSeed.current || sending || !subject) return;
    lastSeed.current = seed.nonce;
    if (onSeedConsumed) onSeedConsumed(seed.nonce);
    send(seed.question, true);
    /* eslint-disable-next-line */
  }, [seed, sending, key]);
  const claimedRef = useRef(new Set());
  // Bound queue: claim the first record whose conversation is free, then send it against its own subject.
  useEffect(() => {
    if (!Array.isArray(queue) || !queue.length || !onClaim) return;
    const rec = queue.find(r => r && r.subject && !claimedRef.current.has(r.id) && !busyFor(keyOf(r.subject)));
    if (!rec) return;
    claimedRef.current.add(rec.id);   // exactly once, even if the host's removal lands a render later
    onClaim(rec.id);
    send(rec.question, true, rec.subject);
    /* eslint-disable-next-line */
  }, [queue, convos, sendingKeys]);
  useEffect(() => { if (autoFocus && inputRef.current) { try { inputRef.current.focus({ preventScroll: true }); } catch (e) { /* ignore */ } } }, [autoFocus, key]);
  const lastMessage = convo.msgs[convo.msgs.length - 1];
  useEffect(() => {
    followStreamRef.current = true;
    window.requestAnimationFrame(() => {
      const transcript = transcriptRef.current;
      if (transcript) transcript.scrollTop = transcript.scrollHeight;
    });
  }, [key]);
  useEffect(() => {
    const transcript = transcriptRef.current;
    if (!transcript || !followStreamRef.current) return;
    transcript.scrollTo({ top: transcript.scrollHeight, behavior: sending ? 'auto' : 'smooth' });
  }, [convo.msgs.length, lastMessage?.markdown?.length, convo.followUps?.length, sending]);

  const changeResponseMode = (event) => {
    const mode = event.target.value;
    setResponseMode(mode);
    try { window.localStorage.setItem('sr-assistant-response-mode', mode); } catch (e) { /* still applies this session */ }
  };

  const resizeInput = (element) => {
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 88)}px`;
  };

  return (
    <div onMouseDownCapture={onInteract}
      style={{ width: full ? '100%' : (overlay ? 'min(420px, 94%)' : dockWidth), height: full ? '100%' : 'auto', flexShrink: 0,
        borderLeft: full ? 'none' : `1px solid ${LINE}`, borderTop: `3px solid ${active ? NAVY : LINE}`,
        background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 0,
        ...(overlay ? { position: 'absolute', top: 0, right: 0, bottom: 0, height: 'auto', zIndex: 20, boxShadow: '-10px 0 30px rgba(15,23,42,.2)' } : {}) }}>
      <style>{`.lr-chat-markdown > :first-child{margin-top:0}.lr-chat-markdown > :last-child{margin-bottom:0}.lr-chat-markdown p{margin:.35rem 0}.lr-chat-markdown h1{font-size:1.25rem;line-height:1.25;margin:.75rem 0 .4rem}.lr-chat-markdown h2{font-size:1.1rem;line-height:1.3;margin:.7rem 0 .35rem}.lr-chat-markdown h3{font-size:1rem;line-height:1.35;margin:.65rem 0 .3rem}.lr-chat-markdown ul,.lr-chat-markdown ol{margin:.4rem 0;padding-left:1.25rem}.lr-chat-markdown li{margin:.2rem 0}.lr-chat-markdown code{font-size:.9em;white-space:pre-wrap}`}</style>
      {/* WRAPS when narrow (owner, 2026-10-15, List view with the chat open): the heading keeps a line
          of its own and the controls drop under it, instead of squeezing the heading to "Ask about / this"
          and the subject to "The whole …". */}
      <div style={{ padding: '8px 10px', borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
        <div style={{ minWidth: 0, flex: '1 1 170px' }}>
          <div style={{ color: NAVY, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{heading}</div>
          <div style={{ color: MUTE, fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {subject?.axisLabel}{subject?.title ? ` · ${subject.title}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
        {usage !== undefined && (
          <span {...tip(usage === null ? 'Unlimited chat on this analysis' : `${usage.remaining} of ${usage.limit} chat messages left`)}
            aria-label={usage === null ? 'Unlimited chat' : `${usage.remaining} of ${usage.limit} chat messages left`}
            style={{ flexShrink: 0, border: `1px solid ${atLimit ? '#c56a6a' : `${NAVY}55`}`, borderRadius: 999,
              padding: '2px 7px', color: atLimit ? '#a94442' : NAVY, background: atLimit ? '#fff0f0' : '#eef4f8',
              fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {usage === null ? '∞' : `${usage.remaining}/${usage.limit}`}
          </span>
        )}
        {Array.isArray(conversations) && onOpenConversation && conversations.length > 0 && (
          <button type="button" onClick={() => setShowList(v => !v)} aria-pressed={showList}
            {...tip('All your conversations on this script')}
            style={{ flexShrink: 0, height: 30, border: `1px solid ${showList ? NAVY : LINE}`, borderRadius: 7, cursor: 'pointer',
              background: showList ? '#eef4f8' : '#fff', color: NAVY, fontWeight: 700, fontSize: 11.5, padding: '0 8px', whiteSpace: 'nowrap' }}>
            Conversations ({conversations.length})
          </button>
        )}
        <select {...tip('Choose response depth')} value={responseMode} onChange={changeResponseMode} aria-label="Response depth"
          style={{ flexShrink: 0, width: 86, height: 30, border: `1px solid ${LINE}`, borderRadius: 7,
            background: '#fff', color: INK, padding: '2px 5px', fontSize: 11.5, fontFamily: 'inherit' }}>
          <option value="concise">Concise</option>
          <option value="detailed">Detailed</option>
          <option value="deep">Deep</option>
        </select>
        <button type="button" onClick={onClose} aria-label={closeLabel} {...tip(closeLabel)}
          style={{ flexShrink: 0, width: 26, height: 26, padding: 0, border: 'none', background: 'none', color: MUTE, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>{closeGlyph}</button>
        </div>
      </div>

      {showList && Array.isArray(conversations) && onOpenConversation ? (
        <ConversationList items={conversations} currentKey={key} onBack={() => setShowList(false)}
          onOpen={(k) => { setShowList(false); onOpenConversation(k); }} />
      ) : (
      <div ref={transcriptRef}
        onScroll={event => {
          const node = event.currentTarget;
          followStreamRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
        }}
        style={{ flex: 1, overflowY: 'auto', padding: '10px', minHeight: 0, background: '#f5f7f9' }}>
        {convo.msgs.length === 0 && (
          <div style={{ color: MUTE, fontSize: 13, lineHeight: 1.55 }}>
            {/* THREE kinds of subject, and this used to have only two branches — so a UNIT+AXIS
                subject fell into the pattern copy and a chat headed "Development · Scene 13" opened
                with "Ask about this pattern" (owner, 2026-08-21). A pattern is a script-wide
                recurrence from the Axis Inspector and carries a `candidateId`; a unit reading is one
                axis on one scene/sequence/act and does not. Key off `candidateId`, not off the
                absence of `_script`. */}
            {subject?.axis === '_script'
              ? <>This chat knows the whole read. Ask anything — everything here is a <b>hypothesis</b> to test against your script, not a verdict.</>
              : subject?.candidateId
                ? <>Ask about this pattern — it’s treated as a <b>hypothesis</b> to test against the actual scenes, not a verdict.</>
                : <>Ask about this reading{subject?.title ? <> of <b>{subject.title}</b></> : null} — it’s a <b>hypothesis</b> to test against your pages, not a verdict.</>}
            {/* static starter chips — no tokens are spent until the writer actually asks */}
            {Array.isArray(starters) && starters.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {starters.map((q, i) => (
                  <button key={i} type="button" onClick={() => send(q, true)} disabled={sending || atLimit}
                    style={{ textAlign: 'left', cursor: (sending || atLimit) ? 'default' : 'pointer', background: '#fff', color: NAVY, fontWeight: 600,
                      fontSize: 12.5, lineHeight: 1.4, border: `1px solid ${NAVY}55`, borderRadius: 10, padding: '8px 11px' }}>
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {convo.msgs.map((m, i) => (
          <div key={m.id || i} style={{ marginBottom: 10, textAlign: m.me ? 'right' : 'left' }}>
            <div style={{ display: 'inline-block', maxWidth: m.me ? '90%' : '96%', textAlign: 'left', padding: '8px 10px', borderRadius: 10,
              border: m.me ? 'none' : `1px solid ${LINE}`, background: m.me ? '#e7f0f5' : '#fff',
              color: INK, fontSize: 13, lineHeight: 1.5, boxShadow: m.me ? 'none' : '0 1px 2px rgba(15,23,42,.04)' }}>
              {m.me ? m.text : m.html
                ? <div dangerouslySetInnerHTML={{ __html: m.html }} />
                : m.markdown !== undefined
                  ? (m.markdown
                    ? <div className="lr-chat-markdown"><ReactMarkdown>{m.markdown}</ReactMarkdown></div>
                    : <span style={{ color: MUTE, fontSize: 12, fontStyle: 'italic' }}>Thinking…</span>)
                  : m.text}
            </div>
          </div>
        ))}
        {!sending && Array.isArray(convo.followUps) && convo.followUps.length > 0 && (
          <div style={{ margin: '2px 0 10px', padding: 8, border: `1px solid ${NAVY}2f`, borderRadius: 10, background: '#fff' }}>
            <div style={{ marginBottom: 6, color: MUTE, fontSize: 10.5, fontWeight: 700, letterSpacing: '.035em', textTransform: 'uppercase' }}>
              Follow-up questions
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {convo.followUps.map((question, index) => (
                <button key={`${question}-${index}`} type="button" onClick={() => send(question)} disabled={sending || atLimit}
                  style={{ width: '100%', border: '1px solid #9ccfc9', borderRadius: 8, background: '#eefaf8',
                    color: '#075f57', padding: '6px 8px', fontSize: 11.5, lineHeight: 1.35,
                    textAlign: 'left', cursor: (sending || atLimit) ? 'default' : 'pointer' }}>
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      <div style={{ borderTop: `1px solid ${LINE}`, padding: 8, display: 'flex', gap: 6 }}>
        <textarea ref={inputRef} value={input} onChange={e => { setInput(e.target.value); resizeInput(e.target); }} rows={1}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder={atLimit ? 'Chat limit reached' : 'Ask a follow-up…'}
          disabled={atLimit}
          style={{ flex: 1, minWidth: 0, minHeight: 38, resize: 'none', overflowY: 'auto', border: `1px solid ${LINE}`,
            borderRadius: 8, padding: '8px 10px', fontSize: 13, lineHeight: 1.35, fontFamily: 'inherit',
            maxHeight: 88, background: atLimit ? '#f4f4f4' : '#fff' }} />
        <button type="button" onClick={() => send(input)} disabled={sending || !input.trim() || atLimit}
          style={{ alignSelf: 'flex-end', height: 38, border: 'none', background: (sending || !input.trim() || atLimit) ? '#9fb3c0' : NAVY,
            color: '#fff', borderRadius: 8, padding: '0 12px', fontWeight: 600,
            cursor: (sending || !input.trim() || atLimit) ? 'default' : 'pointer' }}>{sending ? '…' : 'Send'}</button>
      </div>
    </div>
  );
}

AxisChat.propTypes = {
  movieHash: PropTypes.string,
  subject: PropTypes.object,   // { axis, axisLabel, candidateId, unitId, title }
  onSend: PropTypes.func,
  conversations: PropTypes.array,      // [{ key, label, detail, updatedAt, count }] newest first
  onOpenConversation: PropTypes.func,  // (key) => void — host navigates to that thread       // (subject) => void — called as each question is sent (host remembers the thread)
  seed: PropTypes.object,      // { question, nonce } — auto-sends when nonce changes
  onClose: PropTypes.func,
  starters: PropTypes.arrayOf(PropTypes.string),   // static suggested questions for an empty thread
  active: PropTypes.bool,
  onInteract: PropTypes.func,
  full: PropTypes.bool,
  store: PropTypes.object,
  onSeedConsumed: PropTypes.func,
  heading: PropTypes.string,
  closeGlyph: PropTypes.string,
  closeLabel: PropTypes.string,
  autoFocus: PropTypes.bool,
  queue: PropTypes.array,
  onClaim: PropTypes.func,
};
